import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLearningStage } from '../context/LearningStageContext';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Sparkles, Compass, Map, BookOpen, Star, Target, Rocket, 
  Heart, Award, Brain, ChevronRight, Zap, Sun, Flag
} from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simplified card component for middle school - with explicit colors
const ExplorerCard = ({ icon: Icon, title, description, href, colorClass = "purple", delay = 0 }) => {
  const colorMap = {
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 hover:border-purple-400/50 hover:shadow-purple-500/20',
    blue: 'from-blue-500/20 to-cyan-500/10 border-blue-500/30 hover:border-blue-400/50 hover:shadow-blue-500/20',
    pink: 'from-pink-500/20 to-rose-500/10 border-pink-500/30 hover:border-pink-400/50 hover:shadow-pink-500/20',
    amber: 'from-amber-500/20 to-orange-500/10 border-amber-500/30 hover:border-amber-400/50 hover:shadow-amber-500/20',
    green: 'from-green-500/20 to-emerald-500/10 border-green-500/30 hover:border-green-400/50 hover:shadow-green-500/20',
    cyan: 'from-cyan-500/20 to-blue-500/10 border-cyan-500/30 hover:border-cyan-400/50 hover:shadow-cyan-500/20'
  };
  const iconColorMap = {
    purple: 'bg-purple-500/30 text-purple-400',
    blue: 'bg-blue-500/30 text-blue-400',
    pink: 'bg-pink-500/30 text-pink-400',
    amber: 'bg-amber-500/30 text-amber-400',
    green: 'bg-green-500/30 text-green-400',
    cyan: 'bg-cyan-500/30 text-cyan-400'
  };
  const textColorMap = {
    purple: 'text-purple-400',
    blue: 'text-blue-400',
    pink: 'text-pink-400',
    amber: 'text-amber-400',
    green: 'text-green-400',
    cyan: 'text-cyan-400'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      <Link
        to={href}
        className={`block p-6 rounded-2xl bg-gradient-to-br ${colorMap[colorClass]} border transition-all hover:scale-[1.02] hover:shadow-lg`}
        data-testid={`explorer-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className={`w-14 h-14 rounded-xl ${iconColorMap[colorClass]} flex items-center justify-center mb-4`}>
          <Icon className="w-7 h-7" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
        <p className="text-slate-300 text-sm">{description}</p>
        <div className={`flex items-center gap-1 mt-4 ${textColorMap[colorClass]} text-sm font-medium`}>
          <span>Explore</span>
          <ChevronRight className="w-4 h-4" />
        </div>
      </Link>
    </motion.div>
  );
};

// Daily fun fact component
const DailyFunFact = ({ fact }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="p-4 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30"
  >
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg bg-purple-500/30 flex items-center justify-center flex-shrink-0">
        <Zap className="w-5 h-5 text-purple-400" />
      </div>
      <div>
        <p className="text-purple-300 text-xs font-medium mb-1">Did You Know?</p>
        <p className="text-white text-sm">{fact}</p>
      </div>
    </div>
  </motion.div>
);

// Weekly quest card
const WeeklyQuest = ({ quest, onStart }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    className="p-4 rounded-xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30"
  >
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-xl bg-amber-500/30 flex items-center justify-center">
        <Flag className="w-6 h-6 text-amber-400" />
      </div>
      <div className="flex-1">
        <p className="text-amber-300 text-xs font-medium mb-1">This Week's Quest</p>
        <p className="text-white font-medium">{quest?.title || "Take the Personality Quiz!"}</p>
      </div>
      <Button 
        size="sm" 
        className="bg-amber-500 hover:bg-amber-600 text-white"
        onClick={onStart}
        data-testid="weekly-quest-start-btn"
      >
        Start
      </Button>
    </div>
  </motion.div>
);

// Badge showcase
const BadgeShowcase = ({ badges }) => (
  <div className="flex items-center gap-2">
    {badges.length > 0 ? (
      badges.slice(0, 5).map((badge, i) => (
        <div 
          key={i}
          className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center"
          title={badge.name}
        >
          <Award className="w-5 h-5 text-white" />
        </div>
      ))
    ) : (
      <p className="text-slate-300 text-sm">Complete quests to earn badges!</p>
    )}
  </div>
);

export default function MiddleSchoolHome() {
  const { user, getAuthHeader } = useAuth();
  const { learningStage } = useLearningStage();
  const navigate = useNavigate();
  const [dailyFact, setDailyFact] = useState(null);
  const [badges, setBadges] = useState([]);
  const [weeklyQuest, setWeeklyQuest] = useState(null);
  
  // Fun facts about careers
  const funFacts = [
    "Video game designers combine art, storytelling, and coding to create worlds millions of people explore!",
    "Marine biologists get to work with dolphins, whales, and discover new species in the ocean!",
    "Astronauts train for years in swimming pools to prepare for floating in space!",
    "Some food scientists get paid to taste ice cream and come up with new flavors!",
    "Veterinarians don't just help dogs and cats - some work with elephants, lions, and pandas!",
    "Architects design buildings that can be shaped like anything - even a giant guitar!",
    "Robotics engineers create robots that can dance, play soccer, and even do surgery!",
    "Wildlife photographers travel to amazing places to capture pictures of rare animals!",
    "Animation artists bring characters to life in your favorite movies and shows!",
    "Environmental scientists help protect rainforests, oceans, and endangered animals!"
  ];
  
  useEffect(() => {
    // Pick a random fun fact
    const randomFact = funFacts[Math.floor(Math.random() * funFacts.length)];
    setDailyFact(randomFact);
    
    // Load badges (mock for now)
    setBadges([]);
    
    // Set weekly quest
    setWeeklyQuest({
      title: "Take the Personality Quiz!",
      href: "/ms/quiz"
    });
  }, []);
  
  const firstName = user?.name?.split(' ')[0] || 'Explorer';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
            <Sun className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">
              Hey, {firstName}! 👋
            </h1>
            <p className="text-slate-300">Welcome to your explorer space</p>
          </div>
        </div>
      </motion.div>
      
      {/* Daily Fun Fact */}
      {dailyFact && (
        <div className="mb-6">
          <DailyFunFact fact={dailyFact} />
        </div>
      )}
      
      {/* Weekly Quest */}
      <div className="mb-8">
        <WeeklyQuest 
          quest={weeklyQuest} 
          onStart={() => navigate('/ms/quiz')}
        />
      </div>
      
      {/* Main Explorer Cards */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Compass className="w-5 h-5 text-blue-400" />
          Start Exploring
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ExplorerCard
            icon={Brain}
            title="Who Am I?"
            description="Discover your unique superpowers and what makes you awesome!"
            href="/ms/quiz"
            colorClass="purple"
            delay={0.1}
          />
          <ExplorerCard
            icon={Rocket}
            title="Career Cards"
            description="Explore cool jobs and find out what people actually do every day"
            href="/ms/careers"
            colorClass="blue"
            delay={0.2}
          />
          <ExplorerCard
            icon={Heart}
            title="Dream Life Quiz"
            description="Imagine your perfect day - what would it look like?"
            href="/ms/dream-life"
            colorClass="pink"
            delay={0.3}
          />
        </div>
      </div>
      
      {/* My Adventures Section */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Map className="w-5 h-5 text-amber-400" />
          My Adventures
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <ExplorerCard
            icon={Target}
            title="My Dreams"
            description="Save your goals and dreams - no matter how big!"
            href="/ms/dreams"
            colorClass="amber"
            delay={0.4}
          />
          <ExplorerCard
            icon={Award}
            title="Cool Programs"
            description="Find camps, clubs, and competitions just for you"
            href="/ms/programs"
            colorClass="green"
            delay={0.5}
          />
        </div>
      </div>
      
      {/* Learning Section */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-cyan-400" />
          Learn More
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <ExplorerCard
            icon={BookOpen}
            title="Learning Portal"
            description="Videos, games, and activities to explore careers"
            href="/ms/learn"
            colorClass="cyan"
            delay={0.6}
          />
          <ExplorerCard
            icon={Star}
            title="My Badges"
            description="See all the badges you've earned on your journey"
            href="/ms/badges"
            colorClass="amber"
            delay={0.7}
          />
        </div>
      </div>
      
      {/* Badge Showcase */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="p-4 rounded-xl bg-slate-800/50 border border-slate-700"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-medium mb-1">My Badge Collection</h3>
            <BadgeShowcase badges={badges} />
          </div>
          <Link 
            to="/ms/badges"
            className="text-blue-400 text-sm hover:text-blue-300 flex items-center gap-1"
          >
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </motion.div>
      
      {/* Encouragement Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="mt-8 text-center"
      >
        <p className="text-slate-300 text-sm">
          ✨ Remember: There's no rush! You have plenty of time to figure things out.
        </p>
      </motion.div>
    </div>
  );
}
